import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import useRealtimeSubscription from '../../hooks/useRealtimeSubscription';
import RoleNavigation from '../../components/ui/RoleNavigation';
import NotificationCenter from '../../components/ui/NotificationCenter';
import DashboardQuickActions from '../../components/ui/DashboardQuickActions';
import SocialCentsScoreCard from './components/SocialCentsScoreCard';
import WalletBalanceCard from './components/WalletBalanceCard';
import ActiveCampaignsGrid from './components/ActiveCampaignsGrid';
import SubmissionTrackingPanel from './components/SubmissionTrackingPanel';
import NotificationsFeed from './components/NotificationsFeed';
import PayoutStatusPanel from './components/PayoutStatusPanel';

const UserDashboard = () => {
  const { user, userProfile } = useAuth();

  const [scoreData, setScoreData] = useState({
    score: 0,
    tier: 'Bronze',
    nextTierScore: 200,
    walletBalance: 0,
    pendingRewards: 0,
    recentEarnings: 0,
  });

  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [payoutSummary, setPayoutSummary] = useState({ pendingCount: 0, processingCount: 0, stripeAvailable: 0 });
  const [loading, setLoading] = useState(true);

  // Tier thresholds
  const getTierInfo = (score) => {
    if (score >= 800) return { tier: 'Diamond', nextTierScore: 1000 };
    if (score >= 600) return { tier: 'Platinum', nextTierScore: 800 };
    if (score >= 400) return { tier: 'Gold', nextTierScore: 600 };
    if (score >= 200) return { tier: 'Silver', nextTierScore: 400 };
    return { tier: 'Bronze', nextTierScore: 200 };
  };

  // Load score and wallet
  const loadScoreData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('scores')?.select('total_score, wallet_balance, tier, total_earned')?.eq('user_id', user?.id)?.maybeSingle();

      if (!error && data) {
        const tierInfo = getTierInfo(data?.total_score || 0);
        setScoreData(prev => ({
          ...prev,
          score: data?.total_score || 0,
          tier: data?.tier ? (data?.tier?.charAt(0)?.toUpperCase() + data?.tier?.slice(1)) : tierInfo?.tier,
          nextTierScore: tierInfo?.nextTierScore,
          walletBalance: parseFloat(data?.wallet_balance) || 0,
          recentEarnings: parseFloat(data?.total_earned) || 0,
        }));
      }
    } catch (err) {
      console.error('[UserDashboard] loadScoreData error:', err);
    }
  }, [user?.id]);

  // Load pending wallet balance from wallet_ledger
  const loadPendingBalance = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('wallet_ledger')?.select('amount, ledger_type')?.eq('user_id', user?.id)?.eq('ledger_type', 'credit');

      if (!error && data) {
        // Pending = credits not yet reflected in score wallet_balance
        const pending = data?.reduce((sum, row) => sum + parseFloat(row?.amount || 0), 0);
        setScoreData(prev => ({ ...prev, pendingRewards: pending }));
      }
    } catch (err) {
      console.error('[UserDashboard] loadPendingBalance error:', err);
    }
  }, [user?.id]);

  // Load active campaigns
  const loadCampaigns = useCallback(async () => {
    try {
      const { data, error } = await supabase?.from('campaigns')?.select('id, title, description, reward_amount, budget, spent, min_followers, end_date, categories, status, brand_id')?.eq('status', 'active')?.order('created_at', { ascending: false })?.limit(6);

      if (!error && data) {
        const mapped = data?.map(c => ({
          id: c?.id,
          brandName: c?.title || 'Campaign',
          brandLogo: '',
          brandLogoAlt: `${c?.title} campaign logo`,
          category: c?.categories?.[0] || 'General',
          cashbackPercentage: parseFloat(c?.reward_amount) || 0,
          rewardCap: parseFloat(c?.budget) || 0,
          minFollowers: c?.min_followers || 0,
          endDate: c?.end_date ? new Date(c.end_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Ongoing',
        }));
        setActiveCampaigns(mapped);
      }
    } catch (err) {
      console.error('[UserDashboard] loadCampaigns error:', err);
    }
  }, []);

  // Load submissions
  const loadSubmissions = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('transactions')?.select('id, amount, purchase_value, status, created_at, brand_id, campaign_id, campaigns(title)')?.eq('influencer_id', user?.id)?.order('created_at', { ascending: false })?.limit(10);

      if (!error && data) {
        const mapped = data?.map(t => ({
          id: t?.id,
          brandName: t?.campaigns?.title || 'Brand',
          purchaseAmount: parseFloat(t?.purchase_value) || 0,
          rewardAmount: parseFloat(t?.amount) || 0,
          status: t?.status || 'pending',
          submissionDate: new Date(t.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        }));
        setSubmissions(mapped);
      }
    } catch (err) {
      console.error('[UserDashboard] loadSubmissions error:', err);
    }
  }, [user?.id]);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('notifications')?.select('id, notification_type, title, message, is_read, action_url, created_at')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(20);

      if (!error && data) {
        const mapped = data?.map(n => ({
          id: n?.id,
          type: n?.notification_type || 'system',
          title: n?.title,
          message: n?.message,
          timestamp: new Date(n.created_at),
          unread: !n?.is_read,
          actionLabel: n?.action_url ? 'View Details' : undefined,
        }));
        setNotifications(mapped);
      }
    } catch (err) {
      console.error('[UserDashboard] loadNotifications error:', err);
    }
  }, [user?.id]);

  // Load payout summary for wallet card indicators
  const loadPayoutSummary = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        ?.from('stripe_payouts')
        ?.select('status, amount')
        ?.eq('user_id', user?.id);

      if (!error && data) {
        const pendingCount = data?.filter(p => p?.status === 'pending')?.length || 0;
        const processingCount = data?.filter(p => p?.status === 'processing')?.length || 0;
        const stripeAvailable = data
          ?.filter(p => p?.status === 'paid')
          ?.reduce((sum, p) => sum + parseFloat(p?.amount || 0), 0) || 0;
        setPayoutSummary({ pendingCount, processingCount, stripeAvailable });
      }
    } catch (err) {
      console.error('[UserDashboard] loadPayoutSummary error:', err);
    }
  }, [user?.id]);

  // Initial data load
  useEffect(() => {
    if (!user?.id) return;
    const init = async () => {
      setLoading(true);
      await Promise.all([
        loadScoreData(),
        loadPendingBalance(),
        loadCampaigns(),
        loadSubmissions(),
        loadNotifications(),
        loadPayoutSummary(),
      ]);
      setLoading(false);
    };
    init();
    window.scrollTo(0, 0);
  }, [user?.id]);

  // Real-time: scores table
  useRealtimeSubscription({
    channelName: `user-scores-${user?.id}`,
    table: 'scores',
    filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onUpdate: () => loadScoreData(),
    onInsert: () => loadScoreData(),
  });

  // Real-time: wallet_ledger
  useRealtimeSubscription({
    channelName: `user-wallet-${user?.id}`,
    table: 'wallet_ledger',
    filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onChange: () => {
      loadScoreData();
      loadPendingBalance();
    },
  });

  // Real-time: transactions (submission status updates)
  useRealtimeSubscription({
    channelName: `user-transactions-${user?.id}`,
    table: 'transactions',
    filter: user?.id ? `influencer_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onChange: () => loadSubmissions(),
  });

  // Real-time: notifications
  useRealtimeSubscription({
    channelName: `user-notifications-${user?.id}`,
    table: 'notifications',
    filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onInsert: () => loadNotifications(),
    onUpdate: () => loadNotifications(),
  });

  // Real-time: campaigns
  useRealtimeSubscription({
    channelName: 'user-campaigns',
    table: 'campaigns',
    enabled: true,
    onChange: () => loadCampaigns(),
  });

  // Real-time: stripe_payouts
  useRealtimeSubscription({
    channelName: `user-stripe-payouts-${user?.id}`,
    table: 'stripe_payouts',
    filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onChange: () => loadPayoutSummary(),
  });

  const tierProgress = scoreData?.nextTierScore > 0
    ? Math.round((scoreData?.score % 200) / 200 * 100)
    : 0;

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User';

  return (
    <>
      <Helmet>
        <title>Dashboard - SocialCents | Track Your Rewards & Earnings</title>
        <meta name="description" content="Monitor your SocialCents Score, cashback wallet balance, active campaigns, and reward submissions. Manage your creator-commerce earnings in one place." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleNavigation role="influencer" userName={displayName} />
        
        <div className="pt-[60px]">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="flex flex-col lg:flex-row items-start justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Welcome back, {displayName?.split(' ')?.[0]}! 👋
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Here's your creator-commerce performance overview
                </p>
              </div>
              <NotificationCenter role="influencer" />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
                  <div className="lg:col-span-1">
                    <SocialCentsScoreCard
                      score={scoreData?.score}
                      tier={scoreData?.tier}
                      nextTierScore={scoreData?.nextTierScore}
                      tierProgress={tierProgress} />
                  </div>
                  <div className="lg:col-span-2">
                    <WalletBalanceCard
                      balance={scoreData?.walletBalance}
                      pendingRewards={scoreData?.pendingRewards}
                      recentEarnings={scoreData?.recentEarnings}
                      payoutSummary={payoutSummary} />
                  </div>
                </div>

                <div className="mb-6 md:mb-8">
                  <DashboardQuickActions role="influencer" />
                </div>

                <div className="mb-6 md:mb-8">
                  <ActiveCampaignsGrid campaigns={activeCampaigns} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
                  <div className="lg:col-span-2">
                    <SubmissionTrackingPanel submissions={submissions} />
                  </div>
                  <div className="lg:col-span-1 flex flex-col gap-6">
                    <PayoutStatusPanel userId={user?.id} />
                    <NotificationsFeed notifications={notifications} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserDashboard;